import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box, FormControl, Grid2, IconButton, OutlinedInput } from '@mui/material';
import { isNull } from 'lodash';
import { DeleteIcon, UploadIcon } from './AppIcons';
import { makeStyles } from '@mui/styles';
import theme from '../../../core/theme';

const useStyles = makeStyles({
  uploadArea: {
    borderRadius: '15px',
    '&.small': {
      width: '200px',
      height: '50px',
      paddingTop: '6px',
      border: 'none',
    },
    '&.large': {
      width: '317px',
      height: '210px',
      border: '1px dotted #F67570',
      backgroundColor: '#fff',
    },
  },
  upload: {
    width: '120px',
    height: '36px',
    border: '1px solid #F67570',
    borderRadius: '8px',
    alignItems: 'center',
  },
  close: {
    fontSize: '50px !important',
    '& path': {
      fill: `#F67570 !important`,
    },
  },
  icon: {
    fontSize: '50px !important',
    '& path': {
      fill: `#F67570 !important`,
    },
  },
  smallText: {
    fontSize: '12px',
    color: theme.palette.outlineInactive.main,
  },
  file: {
    width: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  outlineInputField: {
    height: '40px',
    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
});

interface UploaderProps {
  handleFileUpload: (file: File[] | []) => void;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  applyStyles?: boolean;
  id?: string;
  bulkUpload?: boolean;
  isContentUpload?: boolean;
  isPublished?: boolean;
  setSelectedFile?: Dispatch<SetStateAction<string>>;
  readOnly?: boolean;
}

const FileUploader: React.FC<UploaderProps> = ({
  handleFileUpload,
  setErrorMessage,
  applyStyles = true,
  id,
  bulkUpload,
  isContentUpload = false,
  isPublished,
  setSelectedFile,
  readOnly,
}) => {
  const classes = useStyles();
  const [bulkUploadFiles, setBulkUploadFiles] = useState<File[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [file, setFile] = useState<{ name: string; link?: string } | null>(null);

  const ThumbnailExtensions = ['jpg', 'jpeg', 'png', 'svg', 'webp'];
  const AudioExtensions = ['mp3', 'ogg'];
  const DocumentExtensions = ['doc', 'docx', 'txt'];

  function fileValidator(file: File) {
    const fileExtension = file.name.split('.').pop() || '';

    if (id === 'thumbnail' && !ThumbnailExtensions.includes(fileExtension)) {
      setErrorMessage('Please upload a valid image file');
      return {
        code: 'file-type-incorrect',
        message: 'Please upload a valid image file',
      };
    }

    if ((id === 'mfFile' || id === 'voiceover') && !AudioExtensions.includes(fileExtension)) {
      setErrorMessage('Please upload a valid audio file');
      return {
        code: 'file-type-incorrect',
        message: 'Please upload a valid audio file',
      };
    }

    if ((id === 'script' || id === 'subtitle' || id === 'creative_correction') && !DocumentExtensions.includes(fileExtension)) {
      setErrorMessage(`Please upload a valid ${id} document`);
      return {
        code: 'file-type-incorrect',
        message: `Please upload a valid ${id} document`,
      };
    }
    return null;
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');

    if (e.target.files && !bulkUpload) {
      fileValidator(e.target.files[0]);
    }
    if (!isNull(e.target.files) && e.target.files.length > 0) {
      if (bulkUpload && e?.target?.files?.length > 5) {
        setErrorMessage('Number of files allowed to upload is 5');
      }
      if (isContentUpload) {
        bulkUpload ? setBulkUploadFiles((e?.target?.files as unknown) as File[]) : setUploadedFile(e?.target?.files[0]);
        handleFileUpload(((e?.target?.files as unknown) as File[]) || []);
      } else {
        const file = e.target.files?.[0];
        if (file) {
          setFile({ name: file.name });
        }
      }
    }
  };

  useEffect(() => {
    // Wait for the Dropbox SDK to load
    if (typeof window !== 'undefined' && (window as any).Dropbox) {
      return;
    }
  }, []);

  const handleChoose = () => {
    if (!(window as any).Dropbox) {
      setErrorMessage('Dropbox is not loaded. Please try again.');
      return;
    }

    const options = {
      success: (files: any[]) => {
        const file = files[0];
        fileValidator(file);
        setFile({ name: file.name, link: file.link });
        if (setSelectedFile) {
          setSelectedFile(file.link); // ✅ update state
        }
      },
      cancel: () => console.log('User cancelled'),
      linkType: 'preview',
      multiselect: false,
    };

    (window as any).Dropbox.choose(options);
  };

  const onClick = () => {
    setUploadedFile(undefined);
    setBulkUploadFiles([]);
    setFile(null);
    handleFileUpload([]);
    setErrorMessage('');

    if (setSelectedFile) {
      setSelectedFile('');
    }
  };

  return (
    <>
      {isContentUpload ? (
        <>
          <Box className={`${classes.uploadArea} ${applyStyles ? 'large' : 'small'}`}>
            <Grid2 container direction="column" flexWrap="nowrap" alignItems={applyStyles ? 'center' : 'flex-start'}>
              <Grid2 container direction="row" className={classes.upload} mt={applyStyles ? 8 : 0}>
                <Grid2 onClick={onClick}>
                  <UploadIcon viewBox="0 0 42 42" fontSize="large" />
                </Grid2>
                <Grid2>
                  <input
                    id={id ? id : 'files'}
                    type="file"
                    multiple={bulkUpload}
                    onChange={(e) => handleFileChange(e)}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor={id ? id : 'files'}>
                    <span style={{ fontSize: 'h5', textTransform: 'none' }}>Upload file</span>
                  </label>
                </Grid2>
              </Grid2>

              {applyStyles && (
                <Grid2 container direction="column" alignItems="center">
                  <span className="link">Drop or upload file here</span>
                  {id === 'video' && <span className={classes.smallText}>File supported: .mp4, .mov</span>}
                </Grid2>
              )}
            </Grid2>
          </Box>

          <Grid2 container justifyContent="space-between" alignItems="center" mt={1}>
            {bulkUpload ? (
              <>
                {Object.values(bulkUploadFiles).map((file) => (
                  <Grid2 container alignItems="center" wrap="nowrap">
                    <span className={!applyStyles ? classes.file : ''}>{file?.name}</span>
                    <Grid2 container justifyContent="flex-end">
                      <IconButton onClick={onClick} role="button" sx={{ padding: 0 }}>
                        <DeleteIcon className={classes.close} />
                      </IconButton>
                    </Grid2>
                  </Grid2>
                ))}
              </>
            ) : (
              <Grid2 container justifyContent="space-between" alignItems="center" mt={1}>
                <span className={!applyStyles ? classes.file : ''}>{uploadedFile?.name}</span>
                {/* progressbar */}
                {uploadedFile && (
                  <Grid2 container justifyContent="flex-end">
                    <IconButton onClick={onClick} role="button" sx={{ padding: 0 }}>
                      <DeleteIcon className={classes.close} />
                    </IconButton>
                  </Grid2>
                )}
              </Grid2>
            )}
          </Grid2>
        </>
      ) : (
        <Grid2 container flexWrap="nowrap">
          <Grid2 container minWidth={id === 'mfFile' ? '87%' : '88%'}>
            <FormControl sx={{ minWidth: '100%', height: '40px' }}>
              <OutlinedInput className={classes.outlineInputField} placeholder={id} value={uploadedFile?.name || ''} />
            </FormControl>
          </Grid2>
          <Grid2 container>
            <input
              id={id}
              type="file"
              onChange={(e) => handleFileChange(e)}
              disabled={isPublished || readOnly}
              style={{ display: 'none', cursor: isPublished || readOnly ? 'none' : 'pointer' }}
            />
            {!uploadedFile && (
              <label htmlFor={id}>
                <UploadIcon
                  viewBox="0 7 42 42"
                  fontSize="large"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!readOnly) { handleChoose(); }
                  }}
                  className={classes.icon}
                  style={{ cursor: isPublished || readOnly ? 'default' : 'pointer' }}
                />
              </label>
            )}
          </Grid2>
        </Grid2>
      )}
    </>
  );
};

export default FileUploader;
